import React, { useEffect } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import './AidataExtraction.scss';
import { InfoService } from '../../../services/site/InfoService';
import VideoPlayer from '../../../Components/VideoPlayer';
import extractVideo from "../../../assets/videos/extract.mp4";
import extractUseVideo from "../../../assets/videos/extract.mp4";
// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const fadeInLeft = {
  hidden: { opacity: 0, x: -60 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6 } }
};

const fadeInRight = {
  hidden: { opacity: 0, x: 60 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12 }
  }
};

const scaleIn = {
  hidden: { scale: 0.8, opacity: 0 },
  visible: { scale: 1, opacity: 1, transition: { duration: 0.5 } }
};

// Animated section wrapper
const AnimatedSection: React.FC<{ 
  children: React.ReactNode; 
  className?: string;
  variant?: any;
}> = ({ children, className = '', variant = fadeInUp }) => {
  const controls = useAnimation();
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true });

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  return (
    <motion.section
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={variant}
      className={className}
    >
      {children}
    </motion.section>
  );
};

// Hero Section
const HeroSection: React.FC = () => {
  return (
    <AnimatedSection className="sds_ai-hero section">
      <div className="container">
        <div className="columns is-vcentered">
          <div className="column is-6">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="title is-1 sds_ai-hero-title">
                AI Data Extraction Solutions
              </h1>
              <h2 className="subtitle is-4 sds_ai-hero-subtitle mb-5">
                Transform Documents into Actionable Insights + Direct CRM Integration
              </h2>
              <div className="content is-size-5 sds_ai-hero-description">
                <p className="mb-4">
                  <strong>What is AI Data Extraction?</strong> It's intelligent automation that reads your business
                  documents and extracts key information - names, dates, amounts, addresses, and more - then pushes
                  it directly into your CRM or custom systems via API.
                </p>
                <p className="mb-4">
                  Say goodbye to manual data entry! Our AI-powered solution processes resumes, invoices, contracts,
                  forms, and any business document in seconds, then seamlessly integrates with Salesforce, HubSpot,
                  Zoho CRM, or your custom API - saving hours of work and eliminating human errors.
                </p>
              </div>
              <div className="sds_ai-feature-badges">
                <span className="tag is-large sds_ai-badge">🤖 AI-Powered</span>
                <span className="tag is-large sds_ai-badge">🔗 CRM Integration</span>
                <span className="tag is-large sds_ai-badge">⚡ Custom API</span>
                <span className="tag is-large sds_ai-badge">🎯 99% Accurate</span>
              </div>
            </motion.div>
          </div>
          <div className="column is-6">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="sds_ai-hero-visual"
            >
              <div className="sds_ai-animation">
                <motion.div 
                  className="sds_ai-robot-icon"
                  animate={{ 
                    y: [0, -20, 0],
                    rotate: [0, 5, -5, 0]
                  }}
                  transition={{ duration: 4, repeat: Infinity }}
                >
                  🤖
                </motion.div>
                <div className="sds_ai-floating-docs">
                  {['📄', '📊', '📋', '📑'].map((emoji, i) => (
                    <motion.div
                      key={i}
                      className="sds_ai-floating-doc"
                      animate={{ 
                        y: [-15, 15, -15],
                        rotate: [-5, 5, -5]
                      }}
                      transition={{ 
                        duration: 3 + i * 0.5, 
                        repeat: Infinity,
                        delay: i * 0.2 
                      }}
                    >
                      {emoji}
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Featured Video - AI Features & CRM Integration */}
        <VideoPlayer
          videoSrc={extractVideo}
          title="AI Data Extraction Features & CRM Integration"
          description="Discover powerful AI extraction features and seamless CRM integration benefits that transform your document processing workflow"
          lightBackground={false}
        />
      </div>
    </AnimatedSection>
  );
};

// Department Use Cases Section
const DepartmentUseCasesSection: React.FC = () => {
  const departments = [
    { 
      icon: '👥', 
      title: 'HR Department - Resume Processing', 
      color: '#4CAF50',
      desc: 'Automatically extract candidate information from hundreds of resumes in seconds.',
      benefits: [
        'Extract name, contact details, experience',
        'Identify skills and qualifications',
        'Sort candidates by experience level',
        'Create candidate database automatically',
        'Reduce hiring time by 70%'
      ]
    },
    { 
      icon: '💰', 
      title: 'Accounts - Invoice Processing', 
      color: '#FF9800',
      desc: 'Process vendor invoices automatically. Extract amounts, dates, vendor details.',
      benefits: [
        'Extract invoice numbers and amounts',
        'Capture vendor information',
        'Identify due dates and payment terms',
        'Detect duplicate invoices',
        'Speed up approval workflows'
      ]
    },
    { 
      icon: '⚖️', 
      title: 'Legal - Contract Analysis', 
      color: '#9C27B0',
      desc: 'Extract key contract terms, dates, parties, and clauses automatically.',
      benefits: [
        'Identify contract parties and signers',
        'Extract important dates and deadlines',
        'Find key terms and conditions',
        'Track renewal dates',
        'Create contract summaries'
      ]
    },
    { 
      icon: '📈', 
      title: 'Sales - Lead Processing', 
      color: '#F44336',
      desc: 'Convert business cards, inquiry forms into your CRM system instantly.',
      benefits: [
        'Extract contact info from business cards',
        'Process inquiry forms automatically',
        'Capture company details',
        'Identify potential deal values',
        'Update CRM systems in real-time'
      ]
    },
    { 
      icon: '🚛', 
      title: 'Logistics - Shipping Documents', 
      color: '#00BCD4',
      desc: 'Process shipping receipts, delivery notes, and tracking documents.',
      benefits: [
        'Extract tracking numbers and dates',
        'Capture delivery addresses',
        'Process shipping costs',
        'Update delivery status',
        'Generate shipping reports'
      ]
    },
    { 
      icon: '🏥', 
      title: 'Healthcare - Patient Records', 
      color: '#8BC34A',
      desc: 'Extract patient information from forms and medical records securely.',
      benefits: [
        'Process patient registration forms',
        'Extract insurance information',
        'Capture medical history details',
        'Update patient databases',
        'Maintain HIPAA compliance'
      ]
    }
  ];

  return (
    <AnimatedSection className="sds_ai-departments section">
      <div className="container">
        <div className="has-text-centered mb-6">
          <h2 className="title is-2 sds_ai-section-title">How Our AI Helps Different Departments</h2>
          <p className="subtitle is-5">Real-world applications that save time and boost productivity</p>
        </div>

        <motion.div 
          className="columns is-multiline"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          {departments.map((dept, index) => (
            <motion.div key={index} className="column is-4" variants={scaleIn}>
              <motion.div 
                className="sds_ai-dept-card"
                whileHover={{ y: -10 }}
                transition={{ type: "spring", stiffness: 300 }}
                style={{ borderTopColor: dept.color }}
              >
                <div className="sds_ai-dept-icon" style={{ background: `linear-gradient(135deg, ${dept.color}, ${dept.color}dd)` }}>
                  {dept.icon}
                </div>
                <h3 className="title is-5 has-text-centered has-text-white mb-3">{dept.title}</h3>
                <p className="has-text-centered has-text-grey mb-4">{dept.desc}</p>
                <ul className="sds_ai-benefits-list">
                  {dept.benefits.map((benefit, i) => (
                    <li key={i}>
                      <span className="sds_ai-check-icon" style={{ background: dept.color }}>✓</span>
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>

        {/* Featured Video - Real-World Use Cases */}
        <VideoPlayer
          videoSrc={extractUseVideo}
          title="AI Data Extraction Use Cases"
          description="See real-world examples of how businesses use AI extraction across HR, Finance, Legal, Sales, and more departments"
          lightBackground={true}
        />
      </div>
    </AnimatedSection>
  );
};

// CRM Integration Section
const CRMIntegrationSection: React.FC = () => {
  const crmFeatures = [
    {
      icon: '🔄',
      title: 'Seamless CRM Integration',
      color: '#2196F3',
      desc: 'Connect directly with Salesforce, HubSpot, Zoho CRM, and more.',
      benefits: [
        'Auto-populate CRM fields from documents',
        'Bi-directional data synchronization',
        'Real-time updates to CRM records',
        'Custom field mapping',
        'No manual data entry required'
      ]
    },
    {
      icon: '🎯',
      title: 'Lead Management Automation',
      color: '#FF5722',
      desc: 'Extract lead information and push directly to your sales pipeline.',
      benefits: [
        'Business card to CRM in seconds',
        'Inquiry form auto-processing',
        'Lead scoring automation',
        'Duplicate detection',
        'Instant sales notifications'
      ]
    },
    {
      icon: '📊',
      title: 'Customer Data Enrichment',
      color: '#9C27B0',
      desc: 'Enhance customer profiles with extracted document intelligence.',
      benefits: [
        'Extract customer insights from documents',
        'Update contact information automatically',
        'Track document history per customer',
        'Build comprehensive customer profiles',
        'Improve data quality and accuracy'
      ]
    }
  ];

  return (
    <AnimatedSection className="sds_ai-crm section sds_ai-bg-light">
      <div className="container">
        <div className="has-text-centered mb-6">
          <h2 className="title is-2 sds_ai-section-title">
            🔗 Powerful CRM Integration & Custom API
          </h2>
          <p className="subtitle is-5">
            Extract data and sync instantly with your CRM or custom systems
          </p>
        </div>

        <motion.div
          className="columns"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {crmFeatures.map((feature, index) => (
            <motion.div key={index} className="column" variants={fadeInUp}>
              <motion.div
                className="sds_ai-crm-card"
                whileHover={{ y: -10, boxShadow: '0 20px 50px rgba(0,0,0,0.15)' }}
                transition={{ type: "spring", stiffness: 300 }}
                style={{ borderTopColor: feature.color }}
              >
                <div className="sds_ai-crm-icon" style={{ background: `linear-gradient(135deg, ${feature.color}, ${feature.color}dd)` }}>
                  {feature.icon}
                </div>
                <h3 className="title is-4 has-text-centered mb-3">{feature.title}</h3>
                <p className="has-text-centered has-text-grey mb-4">{feature.desc}</p>
                <ul className="sds_ai-benefits-list">
                  {feature.benefits.map((benefit, i) => (
                    <li key={i}>
                      <span className="sds_ai-check-icon" style={{ background: feature.color }}>✓</span>
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>

        {/* Supported CRMs */}
        <div className="has-text-centered mt-6">
          <h3 className="title is-4 mb-4">Integrate with Popular CRMs & Build Custom APIs</h3>
          <div className="sds_ai-crm-logos">
            <motion.div
              className="sds_ai-crm-logo-item"
              whileHover={{ scale: 1.1 }}
            >
              <span className="is-size-3">Salesforce</span>
            </motion.div>
            <motion.div
              className="sds_ai-crm-logo-item"
              whileHover={{ scale: 1.1 }}
            >
              <span className="is-size-3">HubSpot</span>
            </motion.div>
            <motion.div
              className="sds_ai-crm-logo-item"
              whileHover={{ scale: 1.1 }}
            >
              <span className="is-size-3">Zoho CRM</span>
            </motion.div>
            <motion.div
              className="sds_ai-crm-logo-item"
              whileHover={{ scale: 1.1 }}
            >
              <span className="is-size-3">Custom API</span>
            </motion.div>
          </div>
        </div>
      </div>
    </AnimatedSection>
  );
};

// Input Sources Section
const InputSourcesSection: React.FC = () => {
  const sources = [
    {
      icon: '📁',
      title: 'File Upload',
      color: '#4285f4',
      desc: 'Upload files or ZIP folders directly through our secure web portal.',
      benefits: ['PDF, Word, Excel support', 'ZIP folder batch processing', 'Drag and drop interface', 'Progress tracking']
    },
    {
      icon: '📧',
      title: 'Email Integration',
      color: '#ea4335',
      desc: 'Connect Gmail, Outlook to automatically process attachments.',
      benefits: ['Gmail automatic processing', 'Outlook integration', 'Email attachment extraction', 'Real-time processing']
    },
    {
      icon: '☁️',
      title: 'Cloud Storage',
      color: '#34a853',
      desc: 'Connect to Google Drive, Dropbox, OneDrive platforms.',
      benefits: ['Google Drive integration', 'Dropbox connectivity', 'OneDrive access', 'Automatic folder monitoring']
    },
    {
      icon: '🔗',
      title: 'API Access',
      color: '#ff9800',
      desc: 'Integrate directly with your systems using our REST API.',
      benefits: ['REST API integration', 'Webhook support', 'Bulk processing', 'Custom integrations']
    },
    {
      icon: '📱',
      title: 'WhatsApp Integration',
      color: '#25d366',
      desc: 'Send documents through WhatsApp for instant processing.',
      benefits: ['WhatsApp document sharing', 'Mobile-friendly processing', 'Instant results', 'Group chat support']
    },
    {
      icon: '📋',
      title: 'Scanned Documents',
      color: '#9c27b0',
      desc: 'Process scanned images and handwritten forms with OCR.',
      benefits: ['High-quality OCR processing', 'Handwriting recognition', 'Image enhancement', 'Multi-language support']
    }
  ];

  return (
    <AnimatedSection className="sds_ai-inputs section sds_ai-bg-light">
      <div className="container">
        <div className="has-text-centered mb-6">
          <h2 className="title is-2 sds_ai-section-title">Multiple Ways to Send Your Documents</h2>
          <p className="subtitle is-5">We accept documents from wherever you store them</p>
        </div>

        <motion.div 
          className="columns is-multiline"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          {sources.map((source, index) => (
            <motion.div key={index} className="column is-4" variants={fadeInUp}>
              <motion.div 
                className="sds_ai-source-card"
                whileHover={{ y: -8, boxShadow: '0 15px 40px rgba(0,0,0,0.12)' }}
                transition={{ type: "spring", stiffness: 300 }}
                style={{ borderLeftColor: source.color }}
              >
                <div className="sds_ai-source-icon" style={{ background: source.color }}>
                  {source.icon}
                </div>
                <h3 className="title is-5 has-text-centered has-text-white mb-3">{source.title}</h3>
                <p className="has-text-centered has-text-grey mb-4">{source.desc}</p>
                <ul className="sds_ai-benefits-list">
                  {source.benefits.map((benefit, i) => (
                    <li key={i}>
                      <span className="icon has-text-success is-small">
                        <i className="fas fa-check-circle"></i>
                      </span>
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </AnimatedSection>
  );
};

// Output Formats Section
const OutputFormatsSection: React.FC = () => {
  const formats = [
    {
      icon: '📊',
      title: 'Excel Spreadsheets',
      color: '#217346',
      desc: 'Get extracted data in organized Excel files, ready for analysis.',
      benefits: ['Structured data tables', 'Multiple sheets support', 'Custom formatting', 'Charts and graphs']
    },
    {
      icon: '🔗',
      title: 'API Response',
      color: '#0078d4',
      desc: 'Receive structured JSON data directly into your systems.',
      benefits: ['JSON format output', 'Real-time responses', 'Custom field mapping', 'Webhook notifications']
    },
    {
      icon: '📄',
      title: 'CSV Files',
      color: '#ff4444',
      desc: 'Simple CSV format for easy import into any database.',
      benefits: ['Universal compatibility', 'Custom delimiters', 'Bulk export options', 'Database-ready format']
    },
    {
      icon: '🗄️',
      title: 'Database Integration',
      color: '#00acc1',
      desc: 'Direct integration with MySQL, PostgreSQL, MongoDB.',
      benefits: ['Direct database insertion', 'Multiple database support', 'Real-time updates', 'Data validation']
    },
    {
      icon: '📧',
      title: 'Email Reports',
      color: '#795548',
      desc: 'Receive processed data summaries directly in your inbox.',
      benefits: ['Automated email delivery', 'Summary reports', 'Schedule flexibility', 'Multiple recipients']
    },
    {
      icon: '📱',
      title: 'Dashboard View',
      color: '#607d8b',
      desc: 'View and manage extracted data through our web dashboard.',
      benefits: ['Real-time dashboard', 'Search and filter options', 'Export capabilities', 'User management']
    }
  ];

  return (
    <AnimatedSection className="sds_ai-outputs section">
      <div className="container">
        <div className="has-text-centered mb-6">
          <h2 className="title is-2 sds_ai-section-title">Get Your Data in Any Format You Need</h2>
          <p className="subtitle is-5">Flexible output options for seamless integration</p>
        </div>

        <motion.div 
          className="columns is-multiline"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          {formats.map((format, index) => (
            <motion.div key={index} className="column is-4" variants={fadeInUp}>
              <motion.div 
                className="sds_ai-output-card"
                whileHover={{ y: -8 }}
                transition={{ type: "spring", stiffness: 300 }}
                style={{ borderLeftColor: format.color }}
              >
                <div className="sds_ai-output-icon" style={{ background: format.color }}>
                  {format.icon}
                </div>
                <h3 className="title is-5 has-text-white has-text-centered mb-3">{format.title}</h3>
                <p className="has-text-centered has-text-grey mb-4">{format.desc}</p>
                <ul className="sds_ai-benefits-list">
                  {format.benefits.map((benefit, i) => (
                    <li key={i}>
                      <span className="icon has-text-primary is-small">
                        <i className="fas fa-check-circle"></i>
                      </span>
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </AnimatedSection>
  );
};

// Security & Enterprise Section
const SecurityEnterpriseSection: React.FC = () => {
  const features = [
    {
      icon: '🔒',
      title: 'On-Premise Deployment',
      color: '#d32f2f',
      bgGradient: 'linear-gradient(135deg, rgba(211,47,47,0.05), rgba(255,255,255,0.95))',
      desc: 'Install our AI solution on your own servers. Your data never leaves your premises.',
      benefits: [
        'Complete data control',
        'No internet dependency',
        'Custom security policies',
        'Compliance guaranteed',
        'Private cloud options'
      ]
    },
    {
      icon: '🛡️',
      title: 'Bank-Level Security',
      color: '#388e3c',
      bgGradient: 'linear-gradient(135deg, rgba(56,142,60,0.05), rgba(255,255,255,0.95))',
      desc: 'Military-grade encryption, secure data transfer, and comprehensive audit trails.',
      benefits: [
        '256-bit encryption',
        'Secure data transmission',
        'Access control & audit logs',
        'GDPR & HIPAA compliant',
        'Regular security updates'
      ]
    },
    {
      icon: '⚡',
      title: 'Unlimited Processing',
      color: '#1976d2',
      bgGradient: 'linear-gradient(135deg, rgba(25,118,210,0.05), rgba(255,255,255,0.95))',
      desc: 'Process thousands of documents simultaneously without any volume restrictions.',
      benefits: [
        'No document limits',
        'Batch processing support',
        'Auto-scaling infrastructure',
        '24/7 processing capability',
        'High-speed processing'
      ]
    }
  ];

  return (
    <AnimatedSection className="sds_ai-security section sds_ai-bg-gradient">
      <div className="container">
        <div className="has-text-centered mb-6">
          <h2 className="title is-2 sds_ai-section-title has-text-white">
            Enterprise-Grade Security & Unlimited Processing
          </h2>
          <p className="subtitle is-5 has-text-white-ter">Your data stays secure with limitless scalability</p>
        </div>

        <motion.div 
          className="columns"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {features.map((feature, index) => (
            <motion.div key={index} className="column" variants={fadeInUp}>
              <motion.div 
                className="sds_ai-security-card"
                whileHover={{ y: -8 }}
                transition={{ type: "spring", stiffness: 300 }}
                style={{ background: feature.bgGradient, borderLeftColor: feature.color }}
              >
                <div className="sds_ai-security-icon" style={{ background: `linear-gradient(135deg, ${feature.color}, ${feature.color}dd)` }}>
                  {feature.icon}
                </div>
                <h3 className="title is-4 has-text-centered mb-3">{feature.title}</h3>
                <p className="has-text-centered has-text-grey mb-4">{feature.desc}</p>
                <ul className="sds_ai-benefits-list">
                  {feature.benefits.map((benefit, i) => (
                    <li key={i}>
                      <span className="sds_ai-check-icon" style={{ background: feature.color }}>✓</span>
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </AnimatedSection>
  );
};

// CTA Section
const CTASection: React.FC = () => {
  return (
    <AnimatedSection className="sds_ai-cta section">
      <div className="container">
        <div className="box sds_ai-cta-box">
          <div className="has-text-centered">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <h2 className="title has-text-white is-1 mb-5">Ready to Automate Your Document Processing?</h2>
              <p className="subtitle has-text-white is-4 mb-6">
                Join hundreds of businesses who have transformed their operations with AI data extraction
              </p>
              
              <div className="sds_ai-stats mb-6">
                <div className="columns is-mobile is-multiline">
                  <div className="column is-4">
                    <motion.div 
                      className="sds_ai-stat"
                      whileHover={{ scale: 1.05 }}
                    >
                      <p className="sds_ai-stat-number">10x</p>
                      <p className="sds_ai-stat-label">Faster Processing</p>
                    </motion.div>
                  </div>
                  <div className="column is-4">
                    <motion.div 
                      className="sds_ai-stat"
                      whileHover={{ scale: 1.05 }}
                    >
                      <p className="sds_ai-stat-number">99%</p>
                      <p className="sds_ai-stat-label">Accuracy Rate</p>
                    </motion.div>
                  </div>
                  <div className="column is-4">
                    <motion.div 
                      className="sds_ai-stat"
                      whileHover={{ scale: 1.05 }}
                    >
                      <p className="sds_ai-stat-number">70%</p>
                      <p className="sds_ai-stat-label">Cost Reduction</p>
                    </motion.div>
                  </div>
                </div>
              </div>

              <div className="buttons is-centered are-large">
                <motion.a 
                  href="tel:+91 9553896536" 
                  className="button is-primary is-large sds_ai-cta-button sds-call_now_button"
                  whileHover={{ scale: 1.05, y: -3 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span className="icon">
                    <i className="fas fa-phone"></i>
                  </span>
                  <span>Call Now:{InfoService.mobile_no_1}</span>
                </motion.a>
                <motion.a 
                  href="mailto:sds@hdtsolution.com" 
                  className="button is-info is-large sds_ai-cta-button"
                  whileHover={{ scale: 1.05, y: -3 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span className="icon">
                    <i className="fas fa-envelope"></i>
                  </span>
                  <span>Email Us</span>
                </motion.a>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </AnimatedSection>
  );
};

// Main Component
const AIDataExtraction: React.FC = () => {
  return (
    <div className="sds_ai-wrapper">
      {/* SEO Meta Tags */}
      <div style={{ display: 'none' }}>
        <h1>HDT Solutions - AI Data Extraction & Document Processing</h1>
        <meta name="description" content="Automate document data extraction with AI. Process resumes, invoices, contracts automatically. Save time and eliminate errors with intelligent data extraction." />
        <meta name="keywords" content="AI data extraction, document processing, invoice processing, resume parsing, contract analysis, OCR, automated data entry, HDT Solutions" />
      </div>

      <HeroSection />
      <DepartmentUseCasesSection />
      <CRMIntegrationSection />
      <InputSourcesSection />
      <OutputFormatsSection />
      <SecurityEnterpriseSection />
      <CTASection />
     
    </div>
  );
};

export default AIDataExtraction;